/**
 * MedicalDocumentModal - Sistema de Documentos Medicos com Placeholders @
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, Save, Printer, Plus, Trash2, Edit, Copy, BookTemplate,
  User, Calendar, FileSignature, Eye, Search, Clock
} from 'lucide-react';
import PlaceholderTextarea from './PlaceholderTextarea';
import PatientSearchAutocomplete from './PatientSearchAutocomplete';
import {
  PLACEHOLDER_CATEGORIES,
  DEFAULT_DOCUMENT_TEMPLATES,
  buildContextData,
  replacePlaceholders,
  hasUnresolvedPlaceholders,
  getUnresolvedKeys
} from '@/services/placeholderEngine';
import { printDocument } from '@/services/printService';

const DOCUMENT_TYPES = [
  { value: 'atestado', label: 'Atestado Medico' },
  { value: 'declaracao', label: 'Declaracao de Comparecimento' },
  { value: 'laudo', label: 'Laudo Medico' },
  { value: 'encaminhamento', label: 'Encaminhamento' },
  { value: 'relatorio', label: 'Relatorio Medico' },
  { value: 'receita_especial', label: 'Receita Especial' },
  { value: 'outros', label: 'Outros Documentos' }
];

export default function MedicalDocumentModal({ open, onClose, initialPatient }) {
  const [activeTab, setActiveTab] = useState('create');

  // Document creation state
  const [documentType, setDocumentType] = useState('atestado');
  const [titulo, setTitulo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Patient state
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientName, setPatientName] = useState('');
  const [patientCPF, setPatientCPF] = useState('');

  // Extra fields for context
  const [extraFields, setExtraFields] = useState({});

  // Template creation state
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateType, setNewTemplateType] = useState('atestado');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  const [newTemplateBody, setNewTemplateBody] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Templates
  const [templates, setTemplates] = useState([]);

  // History
  const [documentHistory, setDocumentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Permission
  const [canGenerate, setCanGenerate] = useState(true);
  const [loading, setLoading] = useState(false);

  // Current doctor
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (open) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(user);
      fetchTemplates();
      fetchDocumentHistory();
      checkPermission();
      if (initialPatient) {
        setSelectedPatient(initialPatient);
        setPatientName(initialPatient.nome || '');
        setPatientCPF(initialPatient.cpf || '');
      }
    }
  }, [open, initialPatient]);

  const checkPermission = async () => {
    try {
      const r = await axiosInstance.get('/documents/permission/check');
      setCanGenerate(r.data.can_generate);
    } catch { setCanGenerate(false); }
  };

  const fetchTemplates = async () => {
    try {
      const r = await axiosInstance.get('/documents/templates');
      setTemplates(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchDocumentHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axiosInstance.get('/documents/', { params: { limit: 50 } });
      setDocumentHistory(r.data);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  // Build context for placeholder resolution
  const contextData = useMemo(() => {
    const patient = selectedPatient || { nome: patientName, cpf: patientCPF };
    const doctor = currentUser ? { name: currentUser.name || currentUser.nome, crm: currentUser.crm } : null;
    return buildContextData({ patient, doctor, extraFields });
  }, [selectedPatient, patientName, patientCPF, currentUser, extraFields]);

  // Resolved content preview
  const resolvedContent = useMemo(() => {
    return replacePlaceholders(conteudo, contextData);
  }, [conteudo, contextData]);

  const unresolvedKeys = useMemo(() => {
    return getUnresolvedKeys(conteudo, contextData);
  }, [conteudo, contextData]);

  // Handle patient selection
  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientName(patient.nome || '');
    setPatientCPF(patient.cpf || '');
  };

  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setDocumentType(template.tipo);
    setTitulo(template.titulo);
    // Convert old {placeholder} syntax to @PLACEHOLDER
    let body = template.corpo_template || '';
    body = body.replace(/\{paciente_nome\}/g, '@PACIENTE');
    body = body.replace(/\{paciente_cpf\}/g, '@CPF_PACIENTE');
    body = body.replace(/\{data_consulta\}/g, '@DATA');
    body = body.replace(/\{data_documento\}/g, '@DATA_EXTENSO');
    body = body.replace(/\{cidade\}/g, '@CIDADE_CLINICA');
    body = body.replace(/\{nome_profissional\}/g, '@MEDICO');
    body = body.replace(/\{registro_profissional\}/g, '@CRM');
    body = body.replace(/\{cid\}/g, '@CID');
    body = body.replace(/\{dias_afastamento\}/g, '@DIAS');
    body = body.replace(/\{dias_afastamento_extenso\}/g, '@DIAS_EXTENSO');
    body = body.replace(/\{idade\}/g, '@IDADE');
    body = body.replace(/\{data_nascimento\}/g, '@DATA_NASCIMENTO');
    body = body.replace(/\{diagnostico\}/g, '@DIAGNOSTICO');
    body = body.replace(/\{especialidade\}/g, '@ESPECIALIDADE');
    body = body.replace(/\{hipotese_diagnostica\}/g, '@DIAGNOSTICO');
    body = body.replace(/\{historia_clinica\}/g, '@HDA');
    body = body.replace(/\{medicamentos_uso\}/g, '@MEDICAMENTOS');
    body = body.replace(/\{exames_realizados\}/g, '@OBSERVACOES');
    body = body.replace(/\{quadro_clinico\}/g, '@EXAME_FISICO');
    body = body.replace(/\{tratamento\}/g, '@CONDUTA');
    body = body.replace(/\{consideracoes\}/g, '@OBSERVACOES');
    body = body.replace(/\{horario_inicio\}/g, '@HORA');
    body = body.replace(/\{horario_fim\}/g, '@HORA');
    body = body.replace(/\{atividade_fisica\}/g, '@OBSERVACOES');
    body = body.replace(/\{validade\}/g, '@DIAS');
    setConteudo(body);
  };

  // Use a default template
  const handleUseDefaultTemplate = (tmpl) => {
    setDocumentType(tmpl.tipo);
    setTitulo(tmpl.titulo);
    setConteudo(tmpl.corpo_template);
    setSelectedTemplate(null);
  };

  // Reset form
  const resetForm = () => {
    setDocumentType('atestado');
    setTitulo('');
    setConteudo('');
    setSelectedPatient(null);
    setPatientName('');
    setPatientCPF('');
    setPatientSearch('');
    setExtraFields({});
    setSelectedTemplate(null);
    setShowPreview(false);
  };

  // Update extra field
  const updateExtra = (key, val) => {
    setExtraFields(prev => ({ ...prev, [key]: val }));
  };

  // Generate + Print
  const handleGenerate = async () => {
    if (!patientName.trim()) {
      toast.error('Informe o nome do paciente');
      return;
    }
    if (!conteudo.trim()) {
      toast.error('O documento precisa ter conteudo');
      return;
    }

    const finalContent = resolvedContent;

    setLoading(true);
    try {
      const resp = await axiosInstance.post('/documents/', {
        tipo: documentType,
        titulo: titulo || `${DOCUMENT_TYPES.find(t => t.value === documentType)?.label} - ${patientName}`,
        paciente_nome: patientName,
        paciente_cpf: patientCPF || null,
        conteudo: finalContent,
        template_id: selectedTemplate?.id || null,
        cid: extraFields.CID || null,
        dias_afastamento: extraFields.DIAS ? parseInt(extraFields.DIAS) : null,
        observacoes: extraFields.OBSERVACOES || null
      });

      toast.success('Documento gerado com sucesso!');
      printDocument(documentType, finalContent, patientName, resp.data?.id);
      fetchDocumentHistory();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar documento');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintFromHistory = (doc) => {
    printDocument(doc.tipo, doc.conteudo, doc.paciente_nome, doc.id);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
    try {
      await axiosInstance.delete(`/documents/${docId}`);
      toast.success('Documento excluido');
      fetchDocumentHistory();
    } catch (e) {
      toast.error('Erro ao excluir documento');
    }
  };

  if (!canGenerate && open) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Documentos Medicos</DialogTitle>
            <DialogDescription>
              Apenas Medicos, Dentistas e Administradores podem gerar documentos medicos.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl" data-testid="medical-doc-title">
              <FileSignature className="w-5 h-5 text-primary" />
              Documentos Medicos
            </DialogTitle>
            <DialogDescription>
              Crie e gerencie documentos medicos com preenchimento automatico. Use @ para inserir dados.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="create" className="flex-1" data-testid="doc-tab-create">
                <Plus className="w-4 h-4 mr-2" />
                Novo Documento
              </TabsTrigger>
              <TabsTrigger value="templates" className="flex-1" data-testid="doc-tab-templates">
                <BookTemplate className="w-4 h-4 mr-2" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="history" className="flex-1" data-testid="doc-tab-history">
                <Clock className="w-4 h-4 mr-2" />
                Historico
              </TabsTrigger>
            </TabsList>

            {/* ============ CREATE TAB ============ */}
            <TabsContent value="create" className="space-y-5">
              {/* Template Quick Select */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-700 mb-2">Templates Rapidos:</p>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_DOCUMENT_TEMPLATES.map(t => (
                    <Button
                      key={t.tipo + t.titulo}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleUseDefaultTemplate(t)}
                      className="text-xs bg-white hover:bg-blue-100 border-blue-200"
                      data-testid={`quick-template-${t.tipo}`}
                    >
                      {t.titulo}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Document Type + Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Documento</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger className="h-11 mt-1" data-testid="doc-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Titulo</Label>
                  <Input
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Titulo do documento"
                    className="h-11 mt-1"
                    data-testid="doc-titulo-input"
                  />
                </div>
              </div>

              {/* Patient Data */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Dados do Paciente
                </h4>
                <div>
                  <Label className="text-xs">Buscar Paciente Cadastrado</Label>
                  <PatientSearchAutocomplete
                    value={patientSearch}
                    onChange={setPatientSearch}
                    onSelect={handleSelectPatient}
                    placeholder="Buscar paciente por nome..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nome do Paciente *</Label>
                    <Input
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value.toUpperCase())}
                      placeholder="NOME COMPLETO"
                      className="h-10 bg-white"
                      data-testid="doc-patient-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CPF</Label>
                    <Input
                      value={patientCPF}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                        const f = v.length > 9 ? `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6,9)}-${v.slice(9)}` :
                          v.length > 6 ? `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6)}` :
                          v.length > 3 ? `${v.slice(0,3)}.${v.slice(3)}` : v;
                        setPatientCPF(f);
                      }}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      className="h-10 bg-white"
                      data-testid="doc-patient-cpf"
                    />
                  </div>
                </div>
              </div>

              {/* Extra Fields - Contextual */}
              {(documentType === 'atestado' || conteudo.includes('@CID') || conteudo.includes('@DIAS')) && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-amber-700">Campos Adicionais</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {conteudo.includes('@CID') && (
                      <div>
                        <Label className="text-xs">CID-10</Label>
                        <Input value={extraFields.CID || ''} onChange={(e) => updateExtra('CID', e.target.value.toUpperCase())} placeholder="J06.9" className="h-10 bg-white" data-testid="doc-cid" />
                      </div>
                    )}
                    {conteudo.includes('@DIAS') && (
                      <div>
                        <Label className="text-xs">Dias de Afastamento</Label>
                        <Input value={extraFields.DIAS || ''} onChange={(e) => updateExtra('DIAS', e.target.value)} placeholder="3" type="number" className="h-10 bg-white" data-testid="doc-dias" />
                      </div>
                    )}
                    {conteudo.includes('@DIAGNOSTICO') && (
                      <div>
                        <Label className="text-xs">Diagnostico</Label>
                        <Input value={extraFields.DIAGNOSTICO || ''} onChange={(e) => updateExtra('DIAGNOSTICO', e.target.value)} placeholder="Diagnostico" className="h-10 bg-white" data-testid="doc-diagnostico" />
                      </div>
                    )}
                    {conteudo.includes('@ESPECIALIDADE') && (
                      <div>
                        <Label className="text-xs">Especialidade</Label>
                        <Input value={extraFields.ESPECIALIDADE || ''} onChange={(e) => updateExtra('ESPECIALIDADE', e.target.value)} placeholder="Cardiologia" className="h-10 bg-white" data-testid="doc-especialidade" />
                      </div>
                    )}
                    {conteudo.includes('@QUEIXA_PRINCIPAL') && (
                      <div>
                        <Label className="text-xs">Queixa Principal</Label>
                        <Input value={extraFields.QUEIXA_PRINCIPAL || ''} onChange={(e) => updateExtra('QUEIXA_PRINCIPAL', e.target.value)} placeholder="" className="h-10 bg-white" />
                      </div>
                    )}
                    {conteudo.includes('@HDA') && (
                      <div>
                        <Label className="text-xs">Historia da Doenca Atual</Label>
                        <Input value={extraFields.HDA || ''} onChange={(e) => updateExtra('HDA', e.target.value)} placeholder="" className="h-10 bg-white" />
                      </div>
                    )}
                    {conteudo.includes('@CONDUTA') && (
                      <div>
                        <Label className="text-xs">Conduta</Label>
                        <Input value={extraFields.CONDUTA || ''} onChange={(e) => updateExtra('CONDUTA', e.target.value)} placeholder="" className="h-10 bg-white" />
                      </div>
                    )}
                    {conteudo.includes('@EXAME_FISICO') && (
                      <div>
                        <Label className="text-xs">Exame Fisico</Label>
                        <Input value={extraFields.EXAME_FISICO || ''} onChange={(e) => updateExtra('EXAME_FISICO', e.target.value)} placeholder="" className="h-10 bg-white" />
                      </div>
                    )}
                    {conteudo.includes('@MEDICAMENTOS') && (
                      <div>
                        <Label className="text-xs">Medicamentos em Uso</Label>
                        <Input value={extraFields.MEDICAMENTOS || ''} onChange={(e) => updateExtra('MEDICAMENTOS', e.target.value)} placeholder="" className="h-10 bg-white" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Content Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Conteudo do Documento</Label>
                  <Button
                    type="button"
                    variant={showPreview ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    data-testid="doc-preview-toggle"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    {showPreview ? 'Ocultar Preview' : 'Preview'}
                  </Button>
                </div>

                <PlaceholderTextarea
                  value={conteudo}
                  onChange={setConteudo}
                  rows={showPreview ? 8 : 14}
                />

                {/* Unresolved placeholders warning */}
                {unresolvedKeys.length > 0 && conteudo && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                    <span className="font-semibold">Campos nao preenchidos:</span>{' '}
                    {unresolvedKeys.map(k => `@${k}`).join(', ')}
                  </div>
                )}
              </div>

              {/* Live Preview */}
              {showPreview && conteudo && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white" data-testid="doc-preview">
                  <h5 className="text-xs text-gray-500 font-semibold mb-3 uppercase tracking-wider">Pre-visualizacao</h5>
                  <div className="whitespace-pre-wrap font-serif text-sm leading-relaxed text-gray-800">
                    {resolvedContent}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-between border-t pt-4">
                <Button variant="ghost" onClick={resetForm}>Limpar</Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={onClose}>Cancelar</Button>
                  <Button
                    onClick={handleGenerate}
                    disabled={loading || !patientName.trim() || !conteudo.trim()}
                    className="bg-primary"
                    data-testid="doc-generate-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    {loading ? 'Gerando...' : 'Gerar e Imprimir'}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ============ TEMPLATES TAB ============ */}
            <TabsContent value="templates" className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-700">Templates de Documentos</h4>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setShowCreateTemplate(!showCreateTemplate)} data-testid="create-custom-template-btn">
                    <Plus className="w-4 h-4 mr-1" /> {showCreateTemplate ? 'Cancelar' : 'Criar Meu Template'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => seedDefaultTemplates()} data-testid="seed-templates-btn">
                    <BookTemplate className="w-4 h-4 mr-1" /> Templates Padrao
                  </Button>
                </div>
              </div>

              {/* Create Custom Template Form */}
              {showCreateTemplate && (
                <div className="border-2 border-primary/30 rounded-lg p-4 space-y-3 bg-primary/5" data-testid="create-template-form">
                  <h5 className="font-semibold text-primary">Novo Template Personalizado</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nome do Template *</Label>
                      <Input
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        placeholder="Ex: Atestado Personalizado"
                        className="h-10 bg-white"
                        data-testid="custom-template-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select value={newTemplateType} onValueChange={setNewTemplateType}>
                        <SelectTrigger className="h-10 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Descricao (opcional)</Label>
                    <Input
                      value={newTemplateDesc}
                      onChange={(e) => setNewTemplateDesc(e.target.value)}
                      placeholder="Breve descricao do template"
                      className="h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Conteudo do Template (use @ para placeholders)</Label>
                    <PlaceholderTextarea
                      value={newTemplateBody}
                      onChange={setNewTemplateBody}
                      rows={10}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setShowCreateTemplate(false)}>Cancelar</Button>
                    <Button onClick={handleSaveCustomTemplate} disabled={savingTemplate} data-testid="save-custom-template-btn">
                      <Save className="w-4 h-4 mr-1" />
                      {savingTemplate ? 'Salvando...' : 'Salvar Template'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Default templates */}
              <div className="space-y-2 mb-4">
                <h5 className="text-xs font-semibold text-gray-500 uppercase">Templates do Sistema (com @placeholders)</h5>
                {DEFAULT_DOCUMENT_TEMPLATES.map((t, i) => (
                  <div key={i} className="p-3 border rounded-lg hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer flex justify-between items-center"
                    onClick={() => { handleUseDefaultTemplate(t); setActiveTab('create'); }}
                  >
                    <div>
                      <p className="font-medium text-sm">{t.titulo}</p>
                      <p className="text-xs text-gray-500">{t.descricao}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">{t.tipo}</Badge>
                  </div>
                ))}
              </div>

              {/* User templates */}
              {templates.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-xs font-semibold text-gray-500 uppercase">Seus Templates</h5>
                  {templates.map(t => (
                    <div key={t.id} className="p-3 border rounded-lg hover:border-primary/40 transition-colors flex justify-between items-center">
                      <div className="cursor-pointer flex-1" onClick={() => { handleSelectTemplate(t); setActiveTab('create'); }}>
                        <p className="font-medium text-sm">{t.titulo}</p>
                        <p className="text-xs text-gray-500">{t.descricao || 'Sem descricao'}</p>
                      </div>
                      <div className="flex gap-1">
                        <Badge variant="secondary" className="text-xs">{t.tipo}</Badge>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(t.id)} className="h-8 w-8 p-0 text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ============ HISTORY TAB ============ */}
            <TabsContent value="history" className="space-y-4">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Documentos Gerados ({documentHistory.length})
              </h4>
              {historyLoading ? (
                <div className="py-8 text-center text-gray-400">Carregando...</div>
              ) : documentHistory.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  Nenhum documento gerado ainda.
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {documentHistory.map(doc => (
                    <div key={doc.id} className="p-3 border rounded-lg hover:border-gray-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{doc.titulo}</p>
                            <Badge variant="outline" className="text-xs">{doc.tipo}</Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Paciente: {doc.paciente_nome} | {new Date(doc.data_criacao).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handlePrintFromHistory(doc)} className="h-8 w-8 p-0" data-testid={`print-doc-${doc.id}`}>
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteDocument(doc.id)} className="h-8 w-8 p-0 text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );

  async function seedDefaultTemplates() {
    try {
      await axiosInstance.post('/documents/templates/seed-defaults');
      toast.success('Templates padrao criados!');
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar templates');
    }
  }

  async function handleSaveCustomTemplate() {
    if (!newTemplateName.trim()) {
      toast.error('Informe o nome do template');
      return;
    }
    if (!newTemplateBody.trim()) {
      toast.error('O template precisa ter conteudo');
      return;
    }
    setSavingTemplate(true);
    try {
      await axiosInstance.post('/documents/templates', {
        titulo: newTemplateName,
        tipo: newTemplateType,
        descricao: newTemplateDesc || '',
        corpo_template: newTemplateBody,
        campos_editaveis: [],
        is_public: false
      });
      toast.success('Template salvo com sucesso!');
      setShowCreateTemplate(false);
      setNewTemplateName('');
      setNewTemplateType('atestado');
      setNewTemplateDesc('');
      setNewTemplateBody('');
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar template');
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleDeleteTemplate(id) {
    if (!window.confirm('Excluir este template?')) return;
    try {
      await axiosInstance.delete(`/documents/templates/${id}`);
      toast.success('Template excluido');
      fetchTemplates();
    } catch (e) {
      toast.error('Erro ao excluir template');
    }
  }
}
